import { get } from 'lodash';
import { Observable, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  ALLOW_MBC_LOGIN,
  ENABLE_GOOGLE_SIGN_IN,
  ENV,
  IS_LOCAL_ENV,
  IS_QA_ENV,
  SHOW_COUNSELED_YOUTH,
  USE_ROLETYPES_PARAMS,
} from '@config/config';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { authApi } from '@modules/rtk/auth-api';
import { esbApi } from '@modules/rtk/esb-api';
import { observableFromService } from '@modules/shared/utils/observableFromRtkService';
import { toastService } from '@modules/toasts';
import { userPreferencesServices, userServices } from '@shared/duck';
import {
  checkBrowser,
  coreApiService,
  esbApiService,
  googleSignIn,
  jwtDecode,
  personNameBuilder,
} from '@utils';

import unitSrv from '../../unit/duck/services';
import { dedupeRoleTypes } from '../helpers';
import {
  addIsScoutbookRole,
  enhanceWithUnitProfile,
  filterValidOrgs,
  normalizeUserRole,
} from '../helpersTyped';

const defaultAppStorageName = 'DEFAULT_APP';

const noop = () => {};

const googleSignOut$ = (...action) => {
  if (ENABLE_GOOGLE_SIGN_IN) {
    return Observable.create(observer => {
      googleSignIn.signOut();
      return observer.next(...action);
    });
  }
  return Observable.of(...action);
};

const addSessionExp = loginData => {
  const { token } = loginData || {};
  const { exp } = jwtDecode(token);
  return {
    ...loginData,
    exp,
  };
};

/**
 * @param {import('@reduxjs/toolkit').ThunkDispatch} dispatch
 */
const login$ = (login, password, dispatch) => {
  if (featureFlags.getFlag('SBL_5558_NEW_AUTH_API') && dispatch) {
    const thunk = dispatch(
      authApi.endpoints.authenticate.initiate({
        username: login,
        body: { password },
      }),
    )
      .then(result => {
        if (result.error) throw result.error;

        return result.data;
      })
      .catch(error => {
        // idp-102 account locked
        if (error.data?.code === 'client_41') {
          toastService.error(
            'Authentication failed. Please check your credentials and try again later.',
          );
        } else {
          toastService.error(error.data?.message);
        }
        throw error;
      });

    return Observable.fromPromise(thunk).pipe(map(addSessionExp));
  } else {
    return coreApiService
      .post$(
        `/users/${login}/authenticate`,
        { password },
        {
          gtm: {
            label: '/users/{login}/authenticate',
          },
          suppressSentry: [403],
          transformBodyForSentry: () => ({ password: '********' }),
          acceptVersion: '2',
          // temporary workaround
          // conditionally bypass SW if on Safari for non-localhost environments
          skipSw: ENV !== 'local' && checkBrowser.isSafari,
        },
      )
      .pipe(map(addSessionExp));
  }
};

const pointlessSelfsessionGuid = 'A1A1A1A1-B2B2-C3C3-D4D4-E5E5E5E5E5E5';

/**
 * @param {import('@reduxjs/toolkit').ThunkDispatch} dispatch
 * @param {{headers: Record<string, string>}} extraParams
 */
const selfsession$ = (
  extraParams = {},
  isExternalLogin = false,
  dispatch,
  token,
) => {
  const tokenAlt = userServices.retrieveLoginData()?.token;
  if ((IS_QA_ENV || IS_LOCAL_ENV) && !token) {
    // eslint-disable-next-line no-console
    console.debug('Session refresh called without token, using localstorage.', {
      token,
      tokenAlt,
    });
  }
  if (featureFlags.getFlag('SBL_5558_NEW_AUTH_API') && dispatch) {
    const thunk = dispatch(
      authApi.endpoints.sessionRefresh.initiate({ token: token || tokenAlt }),
    )
      .then(result => {
        if (result.error) throw result.error;

        return result.data;
      })
      .catch(error => {
        if (!isExternalLogin && error.status !== 401) {
          toastService.error(error.data?.message || 'Invalid session.');
        }
        throw error;
      });

    return Observable.fromPromise(thunk).pipe(map(addSessionExp));
  } else {
    return coreApiService
      .get$(`/users/self_${pointlessSelfsessionGuid}/sessions/current`, {
        suppressErrorToasts: true,
        suppressSentry: !isExternalLogin ? [401] : [],
        gtm: {
          label: '/users/selfsession',
        },
        ...extraParams,
      })
      .pipe(map(addSessionExp));
  }
};

/**
 * @esbEndpoint GET /persons/v2/:userId/personprofile
 */
const getUserProfileRequest$ = id =>
  esbApiService.get$(`/persons/v2/${id}/personprofile`, {
    gtm: {
      label: '/persons/v2/{userId}/personprofile',
    },
  });

/**
 * @esbEndpoint GET /persons/v2/:personGuid/personprofile
 */
const getCompleteUserProfileRequest$ = (personGuid, { dispatch }) => {
  if (!featureFlags.getFlag('SBL_5499_COMPLETE_PROFILE_API')) {
    return esbApiService.get$(`/persons/v2/${personGuid}/personprofile`, {
      gtm: {
        label: '/persons/v2/{personGuid}/personprofile',
      },
    });
  } else {
    return observableFromService(
      args =>
        dispatch(esbApi.endpoints.getCompletePersonProfile.initiate(args)),
      { personGuid },
    );
  }
};
/**
 * @param {import('@reduxjs/toolkit').ThunkDispatch} dispatch
 */
const masquerade$ = (memberId, dispatch) => {
  if (featureFlags.getFlag('SBL_5610_NEW_MASQUERADE_API') && dispatch) {
    const thunk = dispatch(
      authApi.endpoints.masquerade.initiate({
        memberId,
      }),
    )
      .then(result => {
        if (result.error) throw result.error;

        return result.data;
      })
      .catch(error => {
        if (error.data?.code === 'client_41') {
          toastService.error('Masquerade failed.');
        } else {
          toastService.error(error.data?.message);
        }
        throw error;
      });

    return Observable.fromPromise(thunk).pipe(map(addSessionExp));
  } else {
    return coreApiService
      .get$(`/users/${memberId}/sessions/masquerade`, {
        gtm: {
          label: '/users/{memberId}/masquerade',
        },
      })
      .pipe(map(addSessionExp));
  }
};

const getUserProfile$ = (personGuid, userId, { dispatch }) =>
  forkJoin(
    getCompleteUserProfileRequest$(personGuid, { dispatch }),
    userId ? getUserProfileRequest$(userId) : Observable.of({}),
  ).pipe(
    map(([akelaProfile, sbProfile]) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { organizationPositions = [], ...userProfileData } = akelaProfile;
      const imagePath = get(sbProfile, 'profile.imagePath');
      const {
        organizationPositions: sbOrganizationPositions = [],
        profile: profileFromSB = {},
      } = sbProfile;
      userProfileData.profile = {
        ...userProfileData.profile,
        shortName: personNameBuilder.short(userProfileData.profile),
        fullName: personNameBuilder.full(userProfileData.profile),
        isAdult: profileFromSB.isAdult,
        imagePath,
      };
      userProfileData.sbProfile = sbProfile;
      return {
        ...userProfileData,
        sbOrganizationIds: sbOrganizationPositions.reduce(
          (acc, { organizationGuid, organizationId }) => {
            acc[organizationGuid] = organizationId;
            return acc;
          },
          {},
        ),
      };
    }),
  );

/**
 * @esbEndpoint GET /persons/:personGuid/roleTypes
 */
const getUserRolesRequest$ = (
  id,
  /** @type {import('@reduxjs/toolkit').ThunkDispatch} */
  dispatch,
) => {
  if (featureFlags.getFlag('SBL_5750_ROLETYPES_RTK') && dispatch) {
    return Observable.fromPromise(
      dispatch(
        esbApi.endpoints.getRoleTypes.initiate({
          personGuid: id,
          params: {
            includeParentRoles: 'true',
            includeScoutbookRoles: 'true',
          },
        }),
      ).then(result => {
        if (result.error) throw result;
        return result.data;
      }),
    );
  }
  const params = USE_ROLETYPES_PARAMS
    ? '?includeScoutbookRoles=true&includeParentRoles=true'
    : '';
  return esbApiService.get$(`/persons/${id}/roleTypes${params}`, {
    gtm: {
      label: '/persons/{personGuid}/roleTypes',
    },
  });
};

const getUserRoles$ = (personGuid, userId, { dispatch }) =>
  forkJoin(
    getUserRolesRequest$(personGuid, dispatch),
    userId && !USE_ROLETYPES_PARAMS
      ? getUserRolesRequest$(userId, dispatch)
      : Observable.of([]),
  ).pipe(
    map(([personGuidRoles, userIdRoles]) =>
      addIsScoutbookRole(personGuidRoles, userIdRoles),
    ),
    map(dedupeRoleTypes),
    switchMap(roles =>
      forkJoin(
        Observable.of(roles),
        ...roles
          .filter(role => role.organizationGuid && !role.programTypeId)
          .map(role => unitSrv.getUnitInfo$(role.organizationGuid, dispatch)),
      )
        // eslint-disable-next-line no-console
        .catch(err => console.error('err', err))
        .pipe(
          map(([roles, ...profiles]) =>
            enhanceWithUnitProfile(roles, profiles),
          ),
          map(filterValidOrgs),
          map(roles =>
            roles
              .map(role => normalizeUserRole(role, personGuid))
              .filter(
                ({
                  unitTypeId,
                  isCouncil,
                  isMeritBadgeCounselor,
                  isYouthMember,
                  isParentGuardian,
                  hasLogInRole,
                  organizationGuid,
                }) => {
                  if (!isCouncil && !organizationGuid && !isParentGuardian) {
                    return false;
                  }
                  return (
                    unitTypeId ||
                    isCouncil ||
                    hasLogInRole ||
                    ((SHOW_COUNSELED_YOUTH || ALLOW_MBC_LOGIN) &&
                      isMeritBadgeCounselor) ||
                    isYouthMember ||
                    isParentGuardian
                  );
                },
              ),
          ),
        ),
    ),
  );

/**
 * @esbEndpoint GET /persons/v2/:personGuid/trainings/ypt
 */
const getUserYPTrainingRequest$ = (id, { dispatch }) => {
  if (!featureFlags.getFlag('SBL_5498_YPT_API')) {
    return esbApiService
      .get$(`/persons/v2/${id}/trainings/ypt`, {
        gtm: {
          label: '/persons/v2/{personGuid}/trainings/ypt',
        },
        suppressErrorToasts: [401],
      })
      .pipe(
        catchError(err => {
          if (err.status !== 401) {
            throw err;
          }
          return Observable.of({});
        }),
      );
  } else {
    return Observable.fromPromise(
      dispatch(
        esbApi.endpoints.getPersonTrainingsYPT.initiate({ personGuid: id }),
      ).then(result => {
        if (result.error?.status === 401) return {};

        return result.data;
      }),
    );
  }
};

/**
 * @esbEndpoint GET /persons/:personGuid/subscriptions
 */

const getUsersubscriptionsRequest$ = (personGuid, { dispatch }) => {
  if (!featureFlags.getFlag('SBL_5499_SUBSCRIPTIONS_API')) {
    return esbApiService.get$(`/persons/${personGuid}/subscriptions`, {
      gtm: {
        label: '/persons/{personGuid}/subscriptions',
      },
    });
  } else {
    return observableFromService(
      args => dispatch(esbApi.endpoints.getUserSubscriptions.initiate(args)),
      { personGuid },
    );
  }
};

const getUserData$ = (personGuid, userId, { dispatch }) =>
  forkJoin(
    getUserProfile$(personGuid, userId, { dispatch }),
    getUserRoles$(personGuid, userId, { dispatch }),
    getUserYPTrainingRequest$(personGuid, { dispatch }),
    getUsersubscriptionsRequest$(personGuid, { dispatch }),
  ).pipe(
    map(([profile, organizations, ypTraining, subscriptions]) => {
      const { sbOrganizationIds, ...profileData } = profile;
      return {
        ...profileData,
        ypTraining,
        subscriptions,
        // TODO: move to getUserRoles$
        hasCouncilPosition: organizations.some(({ isCouncil }) => isCouncil),
        // TODO: move to getUserRoles$
        hasCouncilEditRights: organizations.some(
          ({ hasCouncilEditRights }) => hasCouncilEditRights,
        ),
        // TODO: move to getUserRoles$ (need to see how to inject orgGuid)
        organizations: organizations.map(org => {
          const organizationId = sbOrganizationIds[org.organizationGuid];
          return {
            ...org,
            // TODO: move this to a centralized function
            unitId: organizationId,
          };
        }),
      };
    }),
  );

const logout = async (
  params = {
    isDiscourseSessionPreserved: true,
    /** @type {ThunkDispatch} */
    dispatch: null,
  },
) => {
  // SBL-5729 disable momentarily
  //const isDiscourseSessionPreserved = !!params?.isDiscourseSessionPreserved;
  //
  //await webscriptApiService
  //  .get(
  //    `/WebScript/Output/proxy.aspx?Method=ExecuteScriptSet&ScriptSetCode=DiscourseSSOAgent&ScriptCode=ProcessSSOLogout&IsDiscourseSessionPreserved=${
  //      isDiscourseSessionPreserved ? 1 : 0
  //    }`,
  //    {
  //      suppressErrorToasts: true,
  //      suppressSentry: true,
  //      gtm: {
  //        label: '/logout-discourse',
  //      },
  //    },
  //  )
  //  .catch(noop);

  if (featureFlags.getFlag('SBL_5558_NEW_AUTH_API') && params.dispatch) {
    params.dispatch(authApi.endpoints.logout.initiate()).then(noop).catch(noop);
  } else {
    await coreApiService
      .get('/users/logout', {
        suppressErrorToasts: true,
        suppressSentry: true,
        gtm: {
          label: '/logout',
        },
        acceptVersion: false,
      })
      .catch(noop);
  }
};

const storeDefaultApp = (personGuid, app) =>
  userPreferencesServices.storePreferences({
    featureName: defaultAppStorageName,
    personGuid,
    preferences: app,
  });

const retrieveDefaultApp = personGuid =>
  userPreferencesServices.retrievePreferences({
    personGuid,
    featureName: defaultAppStorageName,
  });

export default {
  login$,
  // googleSignInToken$,
  googleSignOut$,
  getUserYPTrainingRequest$,
  getCompleteUserProfileRequest$,
  getUsersubscriptionsRequest$,
  selfsession$,
  masquerade$,
  logout,
  getUserData$,
  getUserRoles$,
  storeDefaultApp,
  retrieveDefaultApp,
  ...userServices,
};
